import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// API Base URL
const BASE_URL = "https://newalemny.3almny.app/api";

// Helper function to safely parse API responses
const safeJsonParse = async (response) => {
  let data = {};
  let responseText = '';
  try {
    responseText = await response.text();
    if (responseText.trim()) {
      data = JSON.parse(responseText);
    }
  } catch (jsonError) {
    console.warn('Failed to parse response as JSON:', responseText);
    // Return empty data object if JSON parsing fails
  }
  return data;
};

// Get subject lessons
export const getSubjectLessons = createAsyncThunk(
  "lessons/getSubjectLessons",
  async ({ subjectId, page = 1, perPage = 15, videoType, freeStatus }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      
      const headers = {
        "Content-Type": "application/json",
      };

      // إضافة التوكن إذا كان موجود
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      // بناء الـ URL مع video_type أو free_status أو بدون فلترة
      let url = `${BASE_URL}/lessons/subject_lessons/${subjectId}?page=${page}&per_page=${perPage}`;
      if (videoType) {
        url += `&video_type=${videoType}`;
      } else if (freeStatus) {
        url += `&free_status=1`;
      }

      // إذا لم يتم إرسال videoType ولا freeStatus، سيتم عرض كل الحصص بدون فلترة

      const response = await fetch(url, {
        headers: headers,
      });
      
      const data = await safeJsonParse(response);

      if (!response.ok) {
        return rejectWithValue(data.message || "حدث خطأ في جلب الدروس");
      }

      return data.result;
    } catch (error) {
      return rejectWithValue("حدث خطأ في الاتصال بالخادم");
    }
  }
);

// Get teacher classes
export const getTeacherClasses = createAsyncThunk(
  "lessons/getTeacherClasses",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) {
        return rejectWithValue("التوكن مطلوب للمعلمين");
      }

      const headers = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      };

      const response = await fetch(`${BASE_URL}/teacher_classes`, {
        headers: headers,
      });
      
      const data = await safeJsonParse(response);

      if (!response.ok) {
        return rejectWithValue(data.message || "حدث خطأ في جلب فصول المعلم");
      }

      return data.result;
    } catch (error) {
      return rejectWithValue("حدث خطأ في الاتصال بالخادم");
    }
  }
);

// Get teacher class lessons
export const getTeacherClassLessons = createAsyncThunk(
  "lessons/getTeacherClassLessons",
  async ({ classId, page = 1, perPage = 15, freeStatus, videoType }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) {
        return rejectWithValue("التوكن مطلوب للمعلمين");
      }

      const headers = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      };

      // بناء الـ URL مع المعاملات
      let url = `${BASE_URL}/lessons/my_class_lessons/${classId}?page=${page}&per_page=${perPage}`;
      if (freeStatus) {
        url += `&free_status=${freeStatus}`;
      } else if (videoType) {
        url += `&video_type=${videoType}`;
      }

      const response = await fetch(url, {
        headers: headers,
      });
      
      const data = await safeJsonParse(response);

      if (!response.ok) {
        return rejectWithValue(data.message || "حدث خطأ في جلب دروس الفصل");
      }

      return {
        data: data.result.data || [],
        pagination: data.result.pagination || null
      };
    } catch (error) {
      return rejectWithValue("حدث خطأ في الاتصال بالخادم");
    }
  }
);

// Get lesson details
export const getLessonDetails = createAsyncThunk(
  "lessons/getLessonDetails",
  async (lessonId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      
      const headers = {
        "Content-Type": "application/json",
      };

      // إضافة التوكن إذا كان موجود
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${BASE_URL}/lessons/student_lesson_details/${lessonId}`, {
        headers: headers,
      });
      
      const data = await safeJsonParse(response);

      if (!response.ok) {
        return rejectWithValue(data.message || "حدث خطأ في جلب تفاصيل الحصة");
      }

      return data.result;
    } catch (error) {
      console.error('Error fetching lesson details:', error);
      return rejectWithValue("حدث خطأ في الاتصال بالخادم");
    }
  }
);

// Get teacher students by class
export const getTeacherStudentsByClass = createAsyncThunk(
  "lessons/getTeacherStudentsByClass",
  async ({ classId, page = 1, perPage = 15 }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) {
        return rejectWithValue("التوكن مطلوب للمعلمين");
      }

      const headers = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      };

      const response = await fetch(`${BASE_URL}/teacher/my_students_by_class?page=${page}&per_page=${perPage}`, {
        headers: headers,
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      console.log('Response ok:', response.ok);
      
      const data = await safeJsonParse(response);
      console.log('Parsed data:', data);

      if (!response.ok) {
        return rejectWithValue(data.message || "حدث خطأ في جلب طلاب الفصل");
      }

      return data.result;
    } catch (error) {
      return rejectWithValue("حدث خطأ في الاتصال بالخادم");
    }
  }
);

// Get parent children
export const getParentChildren = createAsyncThunk(
  "lessons/getParentChildren",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) {
        return rejectWithValue("التوكن مطلوب لولي الأمر");
      }

      const headers = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      };

      const response = await fetch(`${BASE_URL}/parent/parent_childs`, {
        headers: headers,
      });
      
      const data = await safeJsonParse(response);

      if (!response.ok) {
        return rejectWithValue(data.message || "حدث خطأ في جلب الأبناء");
      }

      return data.result;
    } catch (error) {
      console.error('Error fetching parent children:', error);
      return rejectWithValue("حدث خطأ في الاتصال بالخادم");
    }
  }
);

// Get child lessons
export const getChildLessons = createAsyncThunk(
  "lessons/getChildLessons",
  async ({ childId, subjectId = 0, page = 1, perPage = 15000 }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) {
        return rejectWithValue("التوكن مطلوب لولي الأمر");
      }

      const headers = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      };

      const response = await fetch(`${BASE_URL}/parent/child_home_lessons/${subjectId}?page=${page}&per_page=${perPage}&child_id=${childId}`, {
        headers: headers,
      });
      
      const data = await safeJsonParse(response);

      if (!response.ok) {
        return rejectWithValue(data.message || "حدث خطأ في جلب دروس الابن");
      }

      return data.result;
    } catch (error) {
      console.error('Error fetching child lessons:', error);
      return rejectWithValue("حدث خطأ في الاتصال بالخادم");
    }
  }
);

// Get child subjects
export const getChildSubjects = createAsyncThunk(
  "lessons/getChildSubjects",
  async (childId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) {
        return rejectWithValue("التوكن مطلوب");
      }

      const response = await fetch(`${BASE_URL}/parent/child_subjects/${childId}`, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });
      
      const data = await safeJsonParse(response);

      if (!response.ok) {
        return rejectWithValue(data.message || "حدث خطأ في جلب مواد الابن");
      }

      return data.result;
    } catch (error) {
      return rejectWithValue("حدث خطأ في الاتصال بالخادم");
    }
  }
);

// Add new lesson
export const addLesson = createAsyncThunk(
  "lessons/addLesson",
  async (lessonData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) {
        return rejectWithValue("التوكن مطلوب للمعلمين");
      }

      const headers = {
        "Authorization": `Bearer ${token}`,
      };

      const response = await fetch(`${BASE_URL}/lessons/add_new`, {
        method: "POST",
        headers: headers,
        body: lessonData, // FormData
      });
      
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || "حدث خطأ في إضافة الحصة الدراسية");
      }

      return data.result;
    } catch (error) {
      return rejectWithValue("حدث خطأ في الاتصال بالخادم");
    }
  }
);

// Delete lesson
export const deleteLesson = createAsyncThunk(
  "lessons/deleteLesson",
  async (lessonId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) {
        return rejectWithValue("التوكن مطلوب للمعلمين");
      }

      const headers = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      };

      const response = await fetch(`${BASE_URL}/lessons/delete_lesson_file/${lessonId}`, {
        method: "DELETE",
        headers: headers,
      });
      
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || "حدث خطأ في حذف الحصة الدراسية");
      }

      return { lessonId, message: data.message || "تم حذف الحصة بنجاح" };
    } catch (error) {
      return rejectWithValue("حدث خطأ في الاتصال بالخادم");
    }
  }
);

const initialState = {
  subjectLessons: [],
  teacherClasses: [],
  teacherClassLessons: [],
  teacherStudents: [],
  studentsLoading: false,
  studentsError: null,
  studentsPagination: null,
  lessonsLoading: false,
  lessonsError: null,
  lessonsPagination: null,
  lessonDetails: null,
  lessonDetailsLoading: false,
  lessonDetailsError: null,
  addLessonLoading: false,
  addLessonError: null,
  addLessonSuccess: false,
  teacherClassesLoading: false,
  teacherClassesError: null,
  deleteLessonLoading: false,
  deleteLessonError: null,
  deleteLessonSuccess: false,
  // إضافة states جديدة للأبناء
  parentChildren: [],
  parentChildrenLoading: false,
  parentChildrenError: null,
  childLessons: [],
  childLessonsLoading: false,
  childLessonsError: null,
  childLessonsPagination: null,
  childSubjects: [],
  childSubjectsLoading: false,
  childSubjectsError: null,
};

const lessonsSlice = createSlice({
  name: "lessons",
  initialState,
  reducers: {
    clearLessonsError: (state) => {
      state.lessonsError = null;
    },
    clearLessonDetailsError: (state) => {
      state.lessonDetailsError = null;
    },
    resetAddLessonState: (state) => {
      state.addLessonLoading = false;
      state.addLessonError = null;
      state.addLessonSuccess = false;
    },
    resetDeleteLessonState: (state) => {
      state.deleteLessonLoading = false;
      state.deleteLessonError = null;
      state.deleteLessonSuccess = false;
    },
    resetLessons: (state) => {
      state.subjectLessons = [];
      state.teacherClasses = [];
      state.teacherClassLessons = [];
      state.teacherStudents = [];
      state.studentsLoading = false;
      state.studentsError = null;
      state.studentsPagination = null;
      state.lessonsLoading = false;
      state.lessonsError = null;
      state.lessonsPagination = null;
      state.teacherClassesLoading = false;
      state.teacherClassesError = null;
      state.deleteLessonLoading = false;
      state.deleteLessonError = null;
      state.deleteLessonSuccess = false;
    },
    resetLessonDetails: (state) => {
      state.lessonDetails = null;
      state.lessonDetailsLoading = false;
      state.lessonDetailsError = null;
    },
  },
  extraReducers: (builder) => {
    // Get Subject Lessons
    builder
      .addCase(getSubjectLessons.pending, (state) => {
        state.lessonsLoading = true;
        state.lessonsError = null;
      })
      .addCase(getSubjectLessons.fulfilled, (state, action) => {
        state.lessonsLoading = false;
        state.subjectLessons = action.payload.data || [];
        state.lessonsPagination = action.payload.pagination || null;
      })
      .addCase(getSubjectLessons.rejected, (state, action) => {
        state.lessonsLoading = false;
        state.lessonsError = action.payload;
      });

    // Get Teacher Classes
    builder
      .addCase(getTeacherClasses.pending, (state) => {
        state.teacherClassesLoading = true;
        state.teacherClassesError = null;
      })
      .addCase(getTeacherClasses.fulfilled, (state, action) => {
        state.teacherClassesLoading = false;
        state.teacherClasses = action.payload || [];
      })
      .addCase(getTeacherClasses.rejected, (state, action) => {
        state.teacherClassesLoading = false;
        state.teacherClassesError = action.payload;
      });

    // Get Teacher Class Lessons
    builder
      .addCase(getTeacherClassLessons.pending, (state) => {
        state.lessonsLoading = true;
        state.lessonsError = null;
      })
      .addCase(getTeacherClassLessons.fulfilled, (state, action) => {
        state.lessonsLoading = false;
        state.teacherClassLessons = action.payload || { data: [], pagination: null };
      })
      .addCase(getTeacherClassLessons.rejected, (state, action) => {
        state.lessonsLoading = false;
        state.lessonsError = action.payload;
      });

    // Get Lesson Details
    builder
      .addCase(getLessonDetails.pending, (state) => {
        state.lessonDetailsLoading = true;
        state.lessonDetailsError = null;
      })
      .addCase(getLessonDetails.fulfilled, (state, action) => {
        state.lessonDetailsLoading = false;
        state.lessonDetails = action.payload;
      })
      .addCase(getLessonDetails.rejected, (state, action) => {
        state.lessonDetailsLoading = false;
        state.lessonDetailsError = action.payload;
      });

    // Get Teacher Students by Class
    builder
      .addCase(getTeacherStudentsByClass.pending, (state) => {
        state.studentsLoading = true;
        state.studentsError = null;
      })
      .addCase(getTeacherStudentsByClass.fulfilled, (state, action) => {
        state.studentsLoading = false;
        state.teacherStudents = action.payload.data || [];
        state.studentsPagination = action.payload.pagination || null;
      })
      .addCase(getTeacherStudentsByClass.rejected, (state, action) => {
        state.studentsLoading = false;
        state.studentsError = action.payload;
      });

    // Add Lesson
    builder
      .addCase(addLesson.pending, (state) => {
        state.addLessonLoading = true;
        state.addLessonError = null;
      })
      .addCase(addLesson.fulfilled, (state, action) => {
        state.addLessonLoading = false;
        state.addLessonSuccess = true;
      })
      .addCase(addLesson.rejected, (state, action) => {
        state.addLessonLoading = false;
        state.addLessonError = action.payload;
      });

    // Delete Lesson
    builder
      .addCase(deleteLesson.pending, (state) => {
        state.deleteLessonLoading = true;
        state.deleteLessonError = null;
        state.deleteLessonSuccess = false;
      })
      .addCase(deleteLesson.fulfilled, (state, action) => {
        state.deleteLessonLoading = false;
        state.deleteLessonSuccess = true;
        // Remove the deleted lesson from the lists
        if (state.subjectLessons.length > 0) {
          state.subjectLessons = state.subjectLessons.filter(lesson => lesson.id !== action.payload.lessonId);
        }
        if (state.teacherClassLessons.data && state.teacherClassLessons.data.length > 0) {
          state.teacherClassLessons.data = state.teacherClassLessons.data.filter(lesson => lesson.id !== action.payload.lessonId);
        }
      })
      .addCase(deleteLesson.rejected, (state, action) => {
        state.deleteLessonLoading = false;
        state.deleteLessonError = action.payload;
        state.deleteLessonSuccess = false;
      });

    // Get Parent Children
    builder
      .addCase(getParentChildren.pending, (state) => {
        state.parentChildrenLoading = true;
        state.parentChildrenError = null;
      })
      .addCase(getParentChildren.fulfilled, (state, action) => {
        state.parentChildrenLoading = false;
        state.parentChildren = action.payload || [];
      })
      .addCase(getParentChildren.rejected, (state, action) => {
        state.parentChildrenLoading = false;
        state.parentChildrenError = action.payload;
      });

    // Get Child Lessons
    builder
      .addCase(getChildLessons.pending, (state) => {
        state.childLessonsLoading = true;
        state.childLessonsError = null;
      })
      .addCase(getChildLessons.fulfilled, (state, action) => {
        state.childLessonsLoading = false;
        state.childLessons = action.payload.data || [];
        state.childLessonsPagination = action.payload.pagination || null;
      })
      .addCase(getChildLessons.rejected, (state, action) => {
        state.childLessonsLoading = false;
        state.childLessonsError = action.payload;
      });

    // Get Child Subjects
    builder
      .addCase(getChildSubjects.pending, (state) => {
        state.childSubjectsLoading = true;
        state.childSubjectsError = null;
      })
      .addCase(getChildSubjects.fulfilled, (state, action) => {
        state.childSubjectsLoading = false;
        state.childSubjects = action.payload || [];
      })
      .addCase(getChildSubjects.rejected, (state, action) => {
        state.childSubjectsLoading = false;
        state.childSubjectsError = action.payload;
      });
  },
});

export const { clearLessonsError, clearLessonDetailsError, resetAddLessonState, resetDeleteLessonState, resetLessons, resetLessonDetails } = lessonsSlice.actions;
export default lessonsSlice.reducer; 